<template>
    <portal to="sort-options">
        <BtnMenu
            :menu-class="{ 'mb-6 block !w-full': maxSM }"
            class="w-full"
            variant="outlined"
            :options="sortOptions"
            :label="getSortLabel(sort)"
            right-icon="sorting"
            size="sm"
            data-testid="documents-flashcards_sorting-filter"
    /></portal>
    <portal-target
        v-if="maxSM && hasResults"
        name="sort-options"
    />
    <Panel
        v-bind="$attrs"
        :class="[
            '!bg-slate-50 !shadow-none flex flex-col grow bg-background rounded-lg py-6',
            maxSM ? 'px-3' : ' px-6',
        ]"
        :title="$t('documents_and_flashcards')"
    >
        <template #title-prefix>
            <i class="icon icon-course mr-2"></i>
        </template>
        <template
            v-if="minMD"
            #header-options
        >
            <portal-target
                v-if="hasResults"
                name="sort-options"
            />
        </template>
        <div>
            <div class="flex flex-col gap-y-2">
                <div v-if="!isLoading && hasResults">
                    <span class="font-body-2-regular text-content-secondary">{{
                        $t('results', {
                            count:
                                (results.study_materials?.pagination.total ?? 0) +
                                (reFetchedResults.study_materials?.results.length ?? 0),
                        })
                    }}</span>
                </div>
                <Skeleton
                    v-else-if="isLoading"
                    class="w-24"
                    appearance="text"
                />
                <template v-if="results?.study_materials?.results">
                    <CardSearchResult
                        v-for="item in results.study_materials.results"
                        :key="item.id"
                        v-memo="[`${item.id}-${cardMode}`]"
                        :href="getDocumentLink(item.id, item.name, item.type)"
                        :mode="cardMode"
                        :title="item.name"
                        :uni-id="item.university_id"
                        :uni-name="item.university_name"
                        :course-id="item.course_id"
                        :course-name="item.course_name"
                        :img-src="item.preview_link"
                        :type="item.content_type_id"
                        :context="itemContext(item)"
                        :created-at="item.created_at"
                        enable-links
                        clickable
                    />
                </template>
                <template v-if="reFetchedResults.study_materials?.results.length">
                    <div
                        v-if="results?.study_materials?.results.length"
                        class="flex justify-center items-center bg-blue-300 text-slate-400 text-sm px-4 py-3 gap-2 mt-2 rounded font-normal rounded-2xl leading-5 mb-3"
                    >
                        <span><i class="h-6 w-6 icon icon-info text-slate-400 mt-1"></i></span>
                        <p class="flex-1 text-sm text-slate-400 font-medium">
                            {{ $t('search.document_results.few_found') }}
                        </p>
                    </div>
                    <CardSearchResult
                        v-for="item in reFetchedResults.study_materials.results"
                        :key="`secondary-${item.id}`"
                        v-memo="[`${item.id}-${cardMode}`]"
                        :href="getDocumentLink(item.id, item.name, item.type)"
                        :mode="cardMode"
                        :title="item.name"
                        :uni-id="item.university_id"
                        :uni-name="item.university_name"
                        :course-id="item.course_id"
                        :course-name="item.course_name"
                        :img-src="item.preview_link"
                        :type="item.content_type_id"
                        :context="itemContext(item)"
                        :created-at="item.created_at"
                        enable-links
                        clickable
                    />
                </template>
            </div>
            <template v-if="isLoading">
                <Skeleton
                    v-for="i in 10"
                    :key="i"
                    appearance="card"
                    class="h-18 my-1"
                />
            </template>
            <div
                v-if="
                    !isLoading &&
                    !results.study_materials?.results.length &&
                    !reFetchedResults?.study_materials?.results.length
                "
            >
                <h4
                    class="font-body-2-medium pb-1 break-words"
                    v-text="
                        term
                            ? $t('search.no_documents_title', { phrase: term })
                            : $t('search.no_documents_title_default')
                    "
                ></h4>
                <ul class="text-slate-600 pl-5 list-disc font-body-2-regular">
                    <li
                        v-for="(text, index) in noResultTexts"
                        :key="index"
                        v-text="text"
                    />
                </ul>
            </div>
            <div
                v-if="
                    results.study_materials?.pagination.has_more ||
                    reFetchedResults?.study_materials?.pagination.has_more
                "
                class="flex justify-center"
            >
                <BtnCta
                    class="self-center mt-6 w-fit"
                    variant="tertiary"
                    :label="$t('announcement.seeMore')"
                    right-icon="chevron-down"
                    :loading="isLoading"
                    :disabled="isLoading"
                    compact
                    data-testid="documents&flashcards-section_see-more_button"
                    @click="goNextPage"
                />
            </div>
        </div>
    </Panel>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import { useScreenSize } from '@/composables/screenSize';
import {
    type StudyMaterialSearchResponse,
    searchStudyMaterials,
    StudyMaterialsSortType,
} from '@/api/gateway/legacy-api/search';
import type { SearchStudyMaterialDocumentResource as DocumentResultResource } from '@/api/gateway/legacy-api/resources/SearchStudyMaterialDocumentResource';
import type { SearchStudyMaterialFlashcardResource as FlashcardResultResource } from '@/api/gateway/legacy-api/resources/SearchStudyMaterialFlashcardResource';
import Panel from '@/components/Panel';
import CardSearchResult, { type DocumentContext, type FlashcardContext } from '@/components/CardSearchResult';
import type { MenuOption } from '@/components/Menu';
import { getDocumentLink } from '@/router/links';
import type { EmptyObject } from '@/types/misc';
import { isEqual, omit } from 'lodash-es';
import { sortLabel } from '@/utils/searchUtils';
import { type SearchFilters, TrackingContentType } from './SearchIndex';

const INITIAL_PAGE = 0;

export default defineComponent({
    name: 'DocumentResults',
    components: {
        Panel,
        CardSearchResult,
    },
    inheritAttrs: false,
    i18nOptions: {
        namespaces: [
            'search',
            'common',
        ],
    },
    props: {
        /**
         * The term to search for documents and flashcards.
         */
        term: {
            type: String,
            default: undefined,
        },
        /**
         * Search filters to be applied on document and flashcard search results.
         */
        filters: {
            type: Object as PropType<SearchFilters>,
            default: undefined,
        },
        /**
         * Sorting option to be applied on document and flashcard search results.
         */
        sort: {
            type: String as PropType<StudyMaterialsSortType>,
            required: true,
            default: StudyMaterialsSortType.Downloads,
        },
    },
    emits: {
        'update:sort': (sortType: StudyMaterialsSortType) => true, // eslint-disable-line
        'search-finished': (count: number, type: TrackingContentType) => true, // eslint-disable-line
        'has-only-secondary-results': (count: boolean) => true, // eslint-disable-line
    },
    setup: () => {
        const { maxSM, minMD } = useScreenSize();
        return {
            maxSM,
            minMD,
        };
    },
    data: () => ({
        isLoading: true,
        page: INITIAL_PAGE,
        results: {} as StudyMaterialSearchResponse | EmptyObject,
        reFetchedResults: {} as StudyMaterialSearchResponse | EmptyObject,
    }),
    computed: {
        sortOptions(): MenuOption[] {
            return [
                {
                    label: sortLabel(StudyMaterialsSortType.VoteRating),
                    testId: 'sorting-filter_popular',
                    onclick: () => {
                        this.$emit('update:sort', StudyMaterialsSortType.VoteRating);
                    },
                },
                {
                    label: sortLabel(StudyMaterialsSortType.BestMatch),
                    testId: 'sorting-filter_best-match',
                    onclick: () => {
                        this.$emit('update:sort', StudyMaterialsSortType.BestMatch);
                    },
                },
                {
                    label: sortLabel(StudyMaterialsSortType.Downloads),
                    testId: 'sorting-filter_downloads',
                    onclick: () => {
                        this.$emit('update:sort', StudyMaterialsSortType.Downloads);
                    },
                },
                {
                    label: sortLabel(StudyMaterialsSortType.Latest),
                    testId: 'sorting-filter_latest',
                    onclick: () => {
                        this.$emit('update:sort', StudyMaterialsSortType.Latest);
                    },
                },
                {
                    label: sortLabel(StudyMaterialsSortType.Rating),
                    testId: 'sorting-filter_most-liked(rating)',
                    onclick: () => {
                        this.$emit('update:sort', StudyMaterialsSortType.Rating);
                    },
                },
            ];
        },
        cardMode() {
            return this.maxSM ? 'stack' : 'default';
        },
        noResultTexts() {
            return this.$t('search.no_documents_description', { returnObjects: true });
        },
        hasResults() {
            return (
                (this.results.study_materials?.results.length && this.results.study_materials?.pagination.total) ||
                this.reFetchedResults.study_materials?.results.length
            );
        },
    },
    watch: {
        page(): void {
            const isSecondaySearch = !!this.reFetchedResults.study_materials?.results.length;
            this.search(isSecondaySearch);
        },
        sort(): void {
            this.reSearch();
        },
        term(): void {
            this.reSearch();
        },
        filters: {
            handler(filters, oldFilters): void {
                if (isEqual(filters, oldFilters)) return;
                this.reSearch();
            },
            deep: true,
        },
    },
    created() {
        this.search();
    },
    methods: {
        reSearch() {
            this.results = {} as StudyMaterialSearchResponse | EmptyObject;
            this.page = INITIAL_PAGE;
            this.search();
        },
        getDocumentLink,
        processResults(results: StudyMaterialSearchResponse, isSecondary = false): void {
            const targetResults = isSecondary ? this.reFetchedResults : this.results;
            if (this.page === INITIAL_PAGE) {
                if (isSecondary) {
                    this.reFetchedResults = results;
                } else {
                    this.results = results;
                }
            } else {
                targetResults.study_materials.results = targetResults.study_materials.results.concat(
                    results.study_materials.results,
                );
                targetResults.study_materials.pagination = results.study_materials.pagination;
            }
            this.$emit(
                'has-only-secondary-results',
                isSecondary
                    ? !this.results?.study_materials?.results?.length &&
                          this.reFetchedResults?.study_materials?.results?.length > 0
                    : false,
            );
        },
        search(isSecondary = false): void {
            this.isLoading = true;
            const params = this.prepareQueryParams(isSecondary);
            searchStudyMaterials(params)
                .then((response) => {
                    this.processResults(response.data, isSecondary);
                    if (!isSecondary && response.data.study_materials?.results.length < 5 && params?.university_id) {
                        this.search(true);
                    } else if (!isSecondary) {
                        this.reFetchedResults = {};
                    }
                    this.$emit(
                        'search-finished',
                        this.results.study_materials.pagination.total,
                        TrackingContentType.DocumentsFlashcards,
                    );
                })
                .catch((error) => {
                    console.log(error);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        prepareQueryParams(isSecondary = false) {
            const params = {
                term: this.term,
                page: this.page,
                sort: this.sort,
                ...(this.filters?.language_id ? { language_ids: [this.filters.language_id] } : {}),
                ...omit(this.filters, 'language_id'),
            };
            if (isSecondary && params.university_id) {
                params.exclude_university_id = params.university_id;
                delete params.university_id;
            }

            return params;
        },
        goNextPage(): void {
            if (this.reFetchedResults.study_materials) {
                this.page = this.reFetchedResults.study_materials.pagination.current_page + 1;
            } else if (this.results.study_materials) {
                this.page = this.results.study_materials.pagination.current_page + 1;
            }
        },
        itemContext(resultItem: DocumentResultResource | FlashcardResultResource): DocumentContext | FlashcardContext {
            if (resultItem.type === 'flashcard') {
                const context = {} as FlashcardContext;
                context.type = 'flashcard';
                context.id = resultItem.id;
                context.avg_star_score = resultItem.avg_star_score;
                context.ratings_count = resultItem.ratings_count;
                context.learners = resultItem.learners;
                context.user_rated = resultItem.user_rated;
                return context;
            }
            const context = {} as DocumentContext;
            context.type = 'document';
            context.avg_star_score = resultItem.avg_star_score;
            context.ratings_count = resultItem.ratings_count;
            context.questions = resultItem.questions;
            context.downloads = resultItem.downloads;
            context.has_ai_content = resultItem.has_ai_content;
            context.user_rated = resultItem.user_rated;
            return context;
        },
        getSortLabel(sort: StudyMaterialsSortType) {
            return sortLabel(sort);
        },
    },
});
</script>

<style lang="scss" scoped>
.btn-menu-sm::v-deep(.v-popper) {
    width: 100%;
}
</style>
